/**
 * 방 목록 표 + 파기/백업 — §9.2.
 *
 * 관리자 화면(전체 방)과 랜딩의 "내가 연 회의실"(자기 방)이 **같은 표**를
 * 쓴다. 소유자에게도 관리자와 같은 사실과 같은 손잡이를 준다 — 자기 방을
 * 치우려고 관리자를 부를 필요가 없어야 한다는 것이 이 기능의 출발점이다.
 *
 * 파기는 되돌릴 수 없으므로 **안전한 경로를 쉬운 경로로** 만든다: 확인
 * 대화가 "백업 후 종료" 를 기본 행동으로 제시하고, 백업 없이 닫는 것은
 * 한 단계 더 눌러야 하는 선택으로 둔다.
 */

import { useCallback, useState } from 'react'
import { Button } from '@cupia/ui/button'
import { Badge } from '@cupia/ui/badge'
import { Empty } from '@cupia/ui/empty'
import { confirm } from '@cupia/ui/confirm-dialog'
import { Download, LogIn, Mic, MicOff, MonitorX, RefreshCw, Trash2, Users } from 'lucide-react'
import {
  ROOM_STATE_LABELS,
  backupRoom,
  forceCloseRoom,
  formatIdle,
  setRoomDump,
  type RoomSummary,
} from '../lib/rooms-admin'
import { ApiError } from '../lib/api'

export interface RoomTableProps {
  rooms: RoomSummary[]
  /** 목록을 다시 읽는다(조작 후·수동 새로고침) */
  onRefresh: () => void
  onNotify?: (message: string) => void
  /** 소유자 화면은 소유자 열을 숨긴다(전부 자기 것이다) */
  showOwner?: boolean
  /** 백업은 관리자만 — 서버가 `/api/admin/...` 로 막는다 */
  canBackup?: boolean
  /**
   * 음성 녹음 토글을 보여 줄지(관리자 화면만 — S10).
   *
   * 소유자 화면("내가 연 회의실")에는 **일부러 주지 않는다**: 녹음 켜기는
   * 관리 권한이지 참가 자격이 아니다. 회의를 연 사람이 상대측 동의 없이
   * 관세 협상을 녹음하는 손잡이가 되면 안 된다. 서버도 같은 판정을 하므로
   * (`_require_room_admin`) 이건 통제가 아니라 편의다 — 소유자에게 눌러도
   * 403 나는 버튼을 보여 주지 않는 것뿐이다.
   *
   * ⚠️ 녹음 **상태 표시**는 이 값과 무관하게 언제나 보인다(아래). 켜져
   * 있다는 사실을 감추면 S9 가 화면 버전으로 재현된다.
   */
  canRecord?: boolean
  /** 관리자가 뷰어로 참가한다(S22) — 있으면 열린 방에 참가 버튼이 뜬다 */
  onJoin?: (r: RoomSummary) => void
  /**
   * 내 방에 **호스트로 돌아가기**(DEF-131) — 소유자 화면("내가 연 회의실")
   * 에만 준다. 관리자 전체 목록에는 주지 않는다: 관리자는 남의 방 호스트가
   * 될 수 없다(서버 `owns`, S1-b).
   */
  onResume?: (r: RoomSummary) => void
  emptyTitle?: string
  emptyDescription?: string
  /** 자동 새로고침이 돌고 있는지(표시용) */
  autoRefreshing?: boolean
}

export function RoomTable({
  rooms,
  onRefresh,
  onNotify,
  showOwner = true,
  canBackup = false,
  canRecord = false,
  onJoin,
  onResume,
  emptyTitle = '열려 있는 회의실이 없습니다',
  emptyDescription = '진행 중이거나 정리되지 않은 회의실이 여기에 표시됩니다.',
  autoRefreshing = false,
}: RoomTableProps): React.ReactElement {
  const [busy, setBusy] = useState<string | null>(null)

  const notify = useCallback(
    (m: string) => onNotify?.(m),
    [onNotify],
  )

  /** 파기 — 백업을 **기본 경로**로 제시한다(되돌릴 수 없는 조작이다). */
  const onClose = useCallback(
    async (r: RoomSummary) => {
      const hasContent = r.captions > 0 || r.chats > 0
      // 내용이 있는 방이면 백업을 권한다. 빈 방까지 붙잡으면 확인이
      // 형식이 되고, 형식이 된 확인은 아무도 읽지 않는다.
      if (canBackup && hasContent) {
        const backupFirst = await confirm({
          title: `${r.short} 회의실을 파기할까요?`,
          description: `자막 ${r.captions}건, 대화 ${r.chats}건이 있습니다. 파기하면 접속자가 모두 끊기고 되돌릴 수 없습니다. 대화를 먼저 백업하는 것을 권합니다.`,
          confirmLabel: '대화 백업 후 종료',
          cancelLabel: '취소',
        })
        if (!backupFirst) return
        setBusy(r.room)
        try {
          const file = await backupRoom(r.room, 'txt')
          notify(`백업 저장: ${file.filename} (${file.count}건)`)
        } catch (e) {
          // 백업이 실패했으면 **파기하지 않는다** — 안전한 경로를 골랐는데
          // 내용이 사라지면 그 선택이 배신당한 것이다.
          setBusy(null)
          notify(
            e instanceof ApiError
              ? `백업 실패로 파기를 중단했습니다: ${e.message}`
              : '백업에 실패해 파기를 중단했습니다.',
          )
          return
        }
      } else {
        const ok = await confirm({
          title: `${r.short} 회의실을 파기할까요?`,
          description: hasContent
            ? `자막 ${r.captions}건, 대화 ${r.chats}건이 있습니다. 파기하면 접속자가 모두 끊기고 되돌릴 수 없습니다.`
            : '접속자가 모두 끊기고 되돌릴 수 없습니다. 저장된 내용은 없습니다.',
          confirmLabel: '파기',
          cancelLabel: '취소',
          variant: 'destructive',
        })
        if (!ok) return
        setBusy(r.room)
      }

      try {
        await forceCloseRoom(r.room)
        notify('회의실을 파기했습니다.')
        onRefresh()
      } catch (e) {
        notify(e instanceof ApiError ? e.message : '파기하지 못했습니다.')
      } finally {
        setBusy(null)
      }
    },
    [canBackup, notify, onRefresh],
  )

  /**
   * 음성 녹음 켜기/끄기 — S10.
   *
   * **켜기만 확인을 받는다.** 이 화면의 규율은 "되돌릴 수 없는 조작에
   * 확인" 이지만 여기서는 축이 다르다: 켜기는 회의 음성을 디스크에 남기기
   * 시작하는 조작이고(§11 무저장 기본에 대한 예외), 끄기는 그것을 멈추는
   * 조작이다. **끄는 길은 켜는 길보다 쉬워야 한다** — S9 는 정확히 켜기가
   * 쉽고 끄기가 어려워서 생긴 사고다. 끄기에 확인 대화를 붙이면 그 비대칭이
   * 반대로 선다.
   */
  const onToggleDump = useCallback(
    async (r: RoomSummary) => {
      const on = r.dump?.on === true
      if (!on) {
        const ok = await confirm({
          title: `${r.short} 회의의 음성을 녹음할까요?`,
          description:
            '이 회의에서 오가는 **음성 원본**이 서버 디스크에 저장됩니다. ' +
            '자막·대화 기록과는 다른 축입니다. 진단이 끝나면 반드시 꺼 주세요 — ' +
            '켠 사람과 끈 사람이 감사 기록에 남습니다. 저장된 파일은 3일 뒤 자동으로 정리됩니다.',
          confirmLabel: '녹음 시작',
          cancelLabel: '취소',
          variant: 'destructive',
        })
        if (!ok) return
      }
      setBusy(r.room)
      try {
        await setRoomDump(r.room, !on)
        notify(on ? '음성 녹음을 중지했습니다.' : '음성 녹음을 시작했습니다.')
        onRefresh()
      } catch (e) {
        notify(e instanceof ApiError ? e.message : '녹음 설정을 바꾸지 못했습니다.')
      } finally {
        setBusy(null)
      }
    },
    [notify, onRefresh],
  )

  const onBackup = useCallback(
    async (r: RoomSummary) => {
      setBusy(r.room)
      try {
        const file = await backupRoom(r.room, 'txt')
        const note = file.partial ? ' (기록 미보관 방 — 보관 중인 자막만)' : ''
        notify(`백업 저장: ${file.filename} (${file.count}건)${note}`)
      } catch (e) {
        notify(e instanceof ApiError ? e.message : '백업하지 못했습니다.')
      } finally {
        setBusy(null)
      }
    },
    [notify],
  )

  if (rooms.length === 0) {
    return (
      <Empty
        icon={<MonitorX className="h-6 w-6" />}
        title={emptyTitle}
        description={emptyDescription}
        action={
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw className="h-3.5 w-3.5" />
            새로고침
          </Button>
        }
      />
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="text-(length:--text-sm-size) text-[var(--color-muted-foreground)]">
          회의실 {rooms.length}개
          {autoRefreshing && ' · 자동 새로고침 중'}
        </span>
        <Button
          className="ms-auto"
          variant="outline"
          size="sm"
          onClick={onRefresh}
          data-testid="room-refresh"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          새로고침
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table
          className="w-full text-start text-(length:--text-sm-size)"
          data-testid="room-table"
        >
          <thead className="border-b border-[var(--color-border)] text-[var(--color-muted-foreground)]">
            <tr>
              <th className="px-3 py-2 text-start font-medium">회의실</th>
              {showOwner && <th className="px-3 py-2 text-start font-medium">개설자</th>}
              <th className="px-3 py-2 text-start font-medium">상태</th>
              <th className="px-3 py-2 text-start font-medium">참가</th>
              <th className="px-3 py-2 text-start font-medium">내용</th>
              <th className="px-3 py-2 text-start font-medium">마지막 활동</th>
              <th className="px-3 py-2 text-end font-medium">조치</th>
            </tr>
          </thead>
          <tbody>
            {rooms.map((r) => (
              <tr
                key={r.room}
                data-testid={`room-row-${r.room}`}
                className="border-b border-[var(--color-border)] last:border-0"
              >
                <td className="px-3 py-2">
                  <div className="flex flex-col">
                    <span className="font-medium">{r.short}</span>
                    <span className="text-(length:--text-xs-size) text-[var(--color-muted-foreground)]">
                      {r.src.toUpperCase()} → {r.dst.toUpperCase()}
                      {r.record && ' · 기록 중'}
                    </span>
                  </div>
                </td>
                {showOwner && (
                  <td className="px-3 py-2 text-[var(--color-muted-foreground)]">
                    {r.owner || '—'}
                  </td>
                )}
                <td className="px-3 py-2">
                  <div className="flex flex-col gap-1">
                    <Badge
                      variant={
                        r.state === 'live'
                          ? 'softSuccess'
                          : r.state === 'closed'
                            ? 'destructive'
                            : 'softGray'
                      }
                    >
                      {ROOM_STATE_LABELS[r.state] ?? r.state}
                    </Badge>
                    {/* 이 화면의 핵심 신호 — "브라우저를 닫았는데 방이 살아
                        있다" 가 정확히 이 상태다. */}
                    {!r.host_connected && r.state !== 'closed' && (
                      <span
                        className="text-(length:--text-xs-size) text-[var(--color-muted-foreground)]"
                        data-testid={`room-hostoff-${r.room}`}
                      >
                        호스트 미접속
                      </span>
                    )}
                    {/* S10 — **녹음 중이라는 사실은 표에서 바로 보인다.**
                        메뉴 안에 접어 두면 S9 가 화면 버전으로 재현된다:
                        켠 사람은 알지만 다음에 이 화면을 여는 사람은 모른다.
                        `canRecord` 와 무관하게 언제나 그린다 — 끌 권한이
                        없는 사람도 켜져 있다는 것은 알아야 한다. */}
                    {r.dump?.on && (
                      <Badge
                        variant="destructive"
                        data-testid={`room-recording-${r.room}`}
                        title={
                          r.dump.source === 'env'
                            ? '서버 설정(env)으로 전체 녹음이 켜져 있습니다. 화면에서 끌 수 없습니다.'
                            : `${r.dump.actor || '관리자'} 가 켰습니다`
                        }
                      >
                        <Mic className="h-3 w-3" />
                        음성 녹음 중
                        {r.dump.source === 'env' && ' (서버 설정)'}
                        {r.dump.stopped_at_cap && ' · 용량 상한'}
                      </Badge>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-[var(--color-muted-foreground)]">
                  <span className="flex items-center gap-1">
                    <Users className="h-3.5 w-3.5" />
                    {r.viewers}
                  </span>
                </td>
                <td className="px-3 py-2 text-[var(--color-muted-foreground)]">
                  자막 {r.captions} · 대화 {r.chats}
                </td>
                <td className="px-3 py-2 text-[var(--color-muted-foreground)]">
                  {formatIdle(r.idle_s)}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center justify-end gap-1">
                    {onResume && r.state !== 'closed' && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busy !== null}
                        onClick={() => onResume(r)}
                        data-testid={`room-resume-${r.room}`}
                        title="이 회의실에 호스트로 다시 들어갑니다"
                      >
                        <LogIn className="h-3.5 w-3.5" />
                        호스트로 돌아가기
                      </Button>
                    )}
                    {onJoin && r.state !== 'closed' && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busy !== null}
                        onClick={() => onJoin(r)}
                        data-testid={`room-join-${r.room}`}
                        title="이 회의실에 뷰어로 참가합니다"
                      >
                        <LogIn className="h-3.5 w-3.5" />
                        참가
                      </Button>
                    )}
                    {/* S10 — 끄기가 **한 번의 클릭**이어야 한다. env 전역
                        녹음(`source === 'env'`)은 화면에서 끌 수 없으므로
                        버튼을 주지 않는다: 눌러도 안 꺼지는 버튼은 "껐다"
                        고 믿게 만들어 S9 보다 나쁘다. 닫힌 방도 대상이
                        아니다(녹음할 발화가 더는 없다). */}
                    {canRecord &&
                      r.state !== 'closed' &&
                      r.dump?.source !== 'env' && (
                        <Button
                          variant={r.dump?.on ? 'secondary' : 'ghost'}
                          size="sm"
                          disabled={busy !== null}
                          onClick={() => void onToggleDump(r)}
                          data-testid={`room-dump-${r.room}`}
                          title={
                            r.dump?.on
                              ? '이 회의의 음성 녹음을 중지합니다(저장된 파일은 남습니다)'
                              : '이 회의의 음성을 진단용으로 녹음합니다'
                          }
                        >
                          {r.dump?.on ? (
                            <MicOff className="h-3.5 w-3.5" />
                          ) : (
                            <Mic className="h-3.5 w-3.5" />
                          )}
                          {r.dump?.on ? '녹음 중지' : '음성 녹음'}
                        </Button>
                      )}
                    {canBackup && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busy !== null}
                        onClick={() => void onBackup(r)}
                        data-testid={`room-backup-${r.room}`}
                        title="이 회의의 대화를 파일로 저장합니다"
                      >
                        <Download className="h-3.5 w-3.5" />
                        대화 백업
                      </Button>
                    )}
                    {r.state !== 'closed' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy !== null}
                        onClick={() => void onClose(r)}
                        data-testid={`room-close-${r.room}`}
                        title="회의실을 파기합니다(되돌릴 수 없습니다)"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        파기
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
